import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Save, Plus, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FEATURE_GROUPS, ROLE_OPTIONS } from "@/lib/permissions-catalog";
import { getPermissions, savePermissions, renameRole } from "@/lib/api/employees.functions";

export const Route = createFileRoute("/_authenticated/m/configurar-perfil")({
  component: ProfilePermissionsPage,
});

const ALL_FEATURE_IDS = FEATURE_GROUPS.flatMap((g) => g.items.map((i) => i.id));

function ProfilePermissionsPage() {
  const fetchPerms = useServerFn(getPermissions);
  const save = useServerFn(savePermissions);
  const rename = useServerFn(renameRole);

  const [role, setRole] = useState("gerente");
  const [allMap, setAllMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customRoles, setCustomRoles] = useState<{ value: string; label: string }[]>([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [editingLabel, setEditingLabel] = useState("");

  const roleOptions = useMemo(
    () => [...ROLE_OPTIONS.filter((r) => r.value !== "proprietario"), ...customRoles],
    [customRoles],
  );

  const current = useMemo(() => new Set(allMap[role] ?? ALL_FEATURE_IDS), [allMap, role]);

  const addCustomRole = () => {
    const label = newRoleName.trim();
    if (!label) return;
    const value = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (!value) return;
    if (roleOptions.some((r) => r.value === value)) { toast.error("Perfil já existe"); return; }
    setCustomRoles((p) => [...p, { value, label }]);
    setAllMap((p) => ({ ...p, [value]: [] }));
    setRole(value);
    setNewRoleName("");
    toast.success(`Perfil "${label}" criado. Selecione as permissões e clique em Salvar.`);
  };

  const onRename = async () => {
    const label = editingLabel.trim();
    if (!label) return;
    try {
      await rename({ data: { oldRoleName: role, newRoleName: label } });
      const newVal = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      setCustomRoles((prev) => prev.map((r) => (r.value === role ? { value: newVal, label } : r)));
      setAllMap((prev) => {
        const next = { ...prev };
        next[newVal] = next[role] ?? [];
        delete next[role];
        return next;
      });
      setRole(newVal);
      setEditingLabel("");
      toast.success("Perfil renomeado!");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao renomear");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchPerms();
        setAllMap(res.permissions);
        // Restore custom roles saved previously (any key not in the base list).
        const baseValues = new Set(ROLE_OPTIONS.map((r) => r.value));
        const extras = Object.keys(res.permissions ?? {})
          .filter((k) => !baseValues.has(k))
          .map((k) => ({ value: k, label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }));
        if (extras.length) setCustomRoles(extras);
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar permissões");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id: string, on: boolean) => {
    const next = new Set(current);
    if (on) next.add(id);
    else next.delete(id);
    setAllMap({ ...allMap, [role]: Array.from(next) });
  };

  const toggleGroup = (ids: string[], on: boolean) => {
    const next = new Set(current);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setAllMap({ ...allMap, [role]: Array.from(next) });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await save({ data: { roleName: role, permissions: Array.from(current) } });
      toast.success("Perfil salvo!");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-primary" /> Configurar Perfil
        </h1>
        <p className="text-muted-foreground text-sm">
          Defina quais módulos e funcionalidades cada tipo de funcionário pode acessar no sistema.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle>Perfil de Acesso</CardTitle>
            <p className="text-sm text-muted-foreground">Selecione o perfil que deseja configurar, ou crie um novo.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Novo perfil"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomRole())}
              className="w-[180px]"
            />
            <Button type="button" variant="outline" size="sm" onClick={addCustomRole}>
              <Plus className="w-4 h-4 mr-1" /> Adicionar
            </Button>
            {customRoles.some((r) => r.value === role) && (
              <div className="flex items-center gap-1">
                <Input
                  placeholder="Renomear perfil"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onRename())}
                  className="w-[160px]"
                />
                <Button type="button" variant="outline" size="sm" onClick={onRename}>
                  <Pencil className="w-4 h-4 mr-1" /> Renomear
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-40 bg-muted/50 rounded animate-pulse" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
              {FEATURE_GROUPS.map((group) => {
                const groupIds = group.items.map((i) => i.id);
                const allOn = groupIds.every((id) => current.has(id));
                return (
                  <div key={group.label} className="space-y-1">
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-xs font-bold tracking-wider text-muted-foreground">{group.label}</span>
                      <Switch checked={allOn} onCheckedChange={(v) => toggleGroup(groupIds, v)} />
                    </div>
                    {group.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between py-2">
                        <Label htmlFor={`perm-${item.id}`} className="text-sm font-normal">{item.label}</Label>
                        <Switch
                          id={`perm-${item.id}`}
                          checked={current.has(item.id)}
                          onCheckedChange={(v) => toggle(item.id, v)}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving || loading}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Salvando..." : "Salvar Perfil"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        As alterações entram em vigor no próximo login ou recarregamento dos usuários afetados.
      </p>
    </div>
  );
}
